import * as React from "react";
import { VehicleType } from "../../../generated/nest-graphql";
import { EditOutlinedIcon, IconButton, IconButtonEdge, Text, TextVariant, useBrandColors } from "../../ui";
import { describeVehicle, stateCode } from "../vehicleHistoryData";
import { IdentifierBox } from "./IdentifierBox";

/** Named on every tab: the history is one vehicle's, not the customer's. */
export const VehicleHistoryHeader: React.FC<{
  vehicle?: VehicleType | null;
  /** Without it the pencil shows greyed out: there is no vehicle record to edit, or it is busy. */
  onEdit?: () => void;
  /** Why the pencil is greyed out for now, said under the name (a phone shows no tooltips). */
  editHint?: string;
}> = ({ vehicle, onEdit, editHint }) => {
  const { primary } = useBrandColors();
  const plate = vehicle?.licensePlate?.trim();
  const vin = vehicle?.vin?.trim();

  return (
    <div className="px-4 py-3 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Text variant={TextVariant.H6} style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.3, color: "#f8fafc" }}>
          {describeVehicle(vehicle) ?? "This vehicle"}
        </Text>
        <IconButton
          aria-label="Edit vehicle"
          onClick={onEdit}
          disabled={!onEdit}
          edge={IconButtonEdge.END}
          // 44px tap target; grey when there is nothing it can do, so it does not look tappable.
          style={{ padding: 12, flexShrink: 0, color: onEdit ? primary : "#64748b" }}
        >
          <EditOutlinedIcon style={{ fontSize: 20 }} />
        </IconButton>
      </div>
      {editHint && (
        <Text variant={TextVariant.CAPTION} className="text-gray-400" style={{ marginTop: -8 }}>
          {editHint}
        </Text>
      )}
      {/* Both boxes always show once the vehicle is known; while it loads there is nothing to say yet. */}
      {vehicle && (
        // The VIN is the longer of the two, so it gets the wider box.
        <div className="grid gap-2" style={{ gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)" }}>
          <IdentifierBox label="VIN" value={vin} monospace />
          <IdentifierBox label="License plate" value={plate} suffix={stateCode(vehicle.licensePlateState)} />
        </div>
      )}
    </div>
  );
};
